import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MessageSquare, Send, Loader2, CheckCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Feedback = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      toast.error("Please enter your feedback message");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: name.trim() || null,
          email: email.trim() || null,
          category,
          rating: null,
          message: message.trim()
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success("Thank you for your feedback!");
      } else {
        toast.error("Failed to submit feedback");
      }
    } catch (error) {
      toast.error("Error submitting feedback");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setName("");
    setEmail("");
    setCategory("general");
    setMessage("");
    setIsSubmitted(false);
  };

  if (isSubmitted) {
    return (
      <Layout 
        title="Feedback" 
        subtitle="Help us improve evntkit"
      >
        <div className="max-w-xl mx-auto">
          <Card className="tactical-card">
            <CardContent className="p-8 text-center">
              <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
              <p className="text-muted-foreground mb-6">
                Your feedback has been submitted successfully. We appreciate you taking the time to help us improve evntkit.
              </p>
              <Button onClick={resetForm} variant="outline">
                Submit More Feedback
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout 
      title="Feedback" 
      subtitle="Help us improve evntkit"
    >
      <div className="max-w-xl mx-auto">
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MessageSquare className="h-5 w-5 text-cyan-400" />
              Share Your Feedback
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name (optional) */}
              <div className="space-y-2">
                <Label>Name <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="tactical-input"
                  data-testid="feedback-name"
                />
              </div>

              {/* Email (optional) */}
              <div className="space-y-2">
                <Label>Email <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="tactical-input"
                  data-testid="feedback-email"
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="tactical-input" data-testid="feedback-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General Feedback</SelectItem>
                    <SelectItem value="bug">Bug Report</SelectItem>
                    <SelectItem value="feature">Feature Request</SelectItem>
                    <SelectItem value="usability">Usability Issue</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label>Your Feedback <span className="text-destructive">*</span></Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us what you think, report a bug, or suggest a feature..."
                  className="tactical-input min-h-32"
                  required
                  data-testid="feedback-message"
                />
              </div>

              {/* Submit */}
              <Button 
                type="submit" 
                disabled={isSubmitting || !message.trim()}
                className="w-full bg-cyan-600 hover:bg-cyan-700"
                data-testid="submit-feedback"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Feedback
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-4">
          Your feedback helps us build a better tool for event professionals.
        </p>
      </div>
    </Layout>
  );
};

export default Feedback;
